import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './ManageAnnouncements.css';

const ManageAnnouncements = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin';

  const [announcements, setAnnouncements] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    course_id: courseId || '',
    title: '',
    content: '',
    announcement_type: 'info',
    is_pinned: false,
    send_email: false,
    send_sms: false,
    send_whatsapp: false,
    expires_at: '',
    link_url: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [courseId]);

  const fetchData = async () => {
    try {
      const requests = [];

      if (isAdmin) {
        requests.push(api.get('/announcements/admin/all'));
        requests.push(api.get('/courses'));
      } else {
        requests.push(api.get('/announcements/instructor'));
        requests.push(api.get(`/courses?instructor_id=${user.id}`));
      }

      const [announcementsRes, coursesRes] = await Promise.all(requests);
      setAnnouncements(isAdmin ? announcementsRes.data.announcements : announcementsRes.data);
      setCourses(coursesRes.data.courses || coursesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingId) {
        await api.put(`/announcements/${editingId}`, formData);
      } else {
        const endpoint = formData.course_id
          ? `/announcements/course/${formData.course_id}`
          : '/announcements/global';
        await api.post(endpoint, formData);
      }

      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert(t('manageAnnouncements.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      course_id: announcement.course_id || '',
      title: announcement.title,
      content: announcement.content,
      announcement_type: announcement.announcement_type,
      is_pinned: announcement.is_pinned,
      send_email: false,
      send_sms: false,
      send_whatsapp: false,
      expires_at: announcement.expires_at
        ? new Date(announcement.expires_at).toISOString().slice(0, 16)
        : '',
      link_url: announcement.link_url || '',
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('manageAnnouncements.deleteConfirm'))) {
      return;
    }

    try {
      await api.delete(`/announcements/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert(t('manageAnnouncements.deleteFailed'));
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await api.put(`/announcements/${id}/status`, {
        is_active: !currentStatus,
      });
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert(t('manageAnnouncements.statusFailed'));
    }
  };

  const resetForm = () => {
    setFormData({
      course_id: courseId || '',
      title: '',
      content: '',
      announcement_type: 'info',
      is_pinned: false,
      send_email: false,
      send_sms: false,
      send_whatsapp: false,
      expires_at: '',
      link_url: '',
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'info': return '#3b82f6';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      case 'urgent': return '#ef4444';
      default: return '#64748b';
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="page manage-announcements-page">
      <div className="container">
        <header className="page-header">
          <div>
            <h1>{t('manageAnnouncements.title')}</h1>
            <p>{t('manageAnnouncements.subtitle')}</p>
          </div>
          <button
            className="btn-primary"
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              resetForm();
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('manageAnnouncements.newAnnouncement')}
          </button>
        </header>

        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingId ? t('manageAnnouncements.editTitle') : t('manageAnnouncements.createTitle')}</h2>
                <button className="close-btn" onClick={() => setShowForm(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('manageAnnouncements.courseLabel')}</label>
                    <select
                      name="course_id"
                      value={formData.course_id}
                      onChange={handleInputChange}
                      disabled={!!courseId}
                    >
                      {isAdmin && <option value="">{t('manageAnnouncements.globalAllUsers')}</option>}
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>{t('manageAnnouncements.typeLabel')}</label>
                    <select
                      name="announcement_type"
                      value={formData.announcement_type}
                      onChange={handleInputChange}
                    >
                      <option value="info">Info</option>
                      <option value="success">Success</option>
                      <option value="warning">Warning</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('manageAnnouncements.titleLabel')}</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder={t('manageAnnouncements.titlePlaceholder')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('manageAnnouncements.content')}</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder={t('manageAnnouncements.contentPlaceholder')}
                    rows="5"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('manageAnnouncements.meetingLink')}</label>
                  <input
                    type="url"
                    name="link_url"
                    value={formData.link_url}
                    onChange={handleInputChange}
                    placeholder="https://meet.google.com/... or Zoom link"
                  />
                  <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                    {t('manageAnnouncements.meetingLinkNote')}
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('manageAnnouncements.expiresAt')}</label>
                    <input
                      type="datetime-local"
                      name="expires_at"
                      value={formData.expires_at}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_pinned"
                      checked={formData.is_pinned}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark" />
                    {t('manageAnnouncements.pinAnnouncement')}
                  </label>
                </div>

                <div className="notification-options">
                  <h4>{t('manageAnnouncements.notificationsTitle')}</h4>
                  <p className="helper-text">{t('manageAnnouncements.notificationsHelper')}</p>
                  <div className="checkbox-row">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="send_email"
                        checked={formData.send_email}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark" />
                      {t('manageAnnouncements.emailLabel')}
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="send_sms"
                        checked={formData.send_sms}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark" />
                      {t('manageAnnouncements.smsLabel')}
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="send_whatsapp"
                        checked={formData.send_whatsapp}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark" />
                      {t('manageAnnouncements.whatsappLabel')}
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowForm(false)}
                  >
                    {t('manageAnnouncements.cancel')}
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? t('manageAnnouncements.saving') : editingId ? t('manageAnnouncements.update') : t('manageAnnouncements.publish')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="announcements-table">
          <div className={`table-header ${isAdmin ? 'with-status' : ''}`}>
            <span>{t('manageAnnouncements.announcementCol')}</span>
            <span>{t('manageAnnouncements.courseCol')}</span>
            <span>{t('manageAnnouncements.typeCol')}</span>
            {isAdmin && <span>{t('manageAnnouncements.statusCol')}</span>}
            <span>{t('manageAnnouncements.publishedCol')}</span>
            <span>{t('manageAnnouncements.actionsCol')}</span>
          </div>

          {announcements.length === 0 ? (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <p>{t('manageAnnouncements.noAnnouncements')}</p>
              <button className="btn-primary" onClick={() => setShowForm(true)}>
                {t('manageAnnouncements.createFirst')}
              </button>
            </div>
          ) : (
            announcements.map((announcement) => (
              <div key={announcement.id} className={`table-row ${isAdmin ? 'with-status' : ''} ${announcement.is_active === false ? 'inactive' : ''}`}>
                <div className="announcement-cell">
                  <div className="announcement-info">
                    {announcement.is_pinned && (
                      <span className="pin-icon" title="Pinned">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                        </svg>
                      </span>
                    )}
                    <div>
                      <h4>{announcement.title}</h4>
                      <p>{announcement.content.substring(0, 80)}...</p>
                    </div>
                  </div>
                </div>
                <div className="course-cell">
                  {announcement.course_title || (
                    <span className="global-badge">{t('manageAnnouncements.globalBadge')}</span>
                  )}
                </div>
                <div className="type-cell">
                  <span
                    className="type-badge"
                    style={{ backgroundColor: getTypeColor(announcement.announcement_type) + '20', color: getTypeColor(announcement.announcement_type) }}
                  >
                    {announcement.announcement_type}
                  </span>
                </div>
                {isAdmin && (
                  <div className="status-cell">
                    <button
                      className={`status-toggle ${announcement.is_active !== false ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus(announcement.id, announcement.is_active !== false)}
                    >
                      <span className="toggle-track">
                        <span className="toggle-thumb" />
                      </span>
                      <span className="toggle-label">
                        {announcement.is_active !== false ? t('manageAnnouncements.active') : t('manageAnnouncements.inactive')}
                      </span>
                    </button>
                  </div>
                )}
                <div className="date-cell">
                  {new Date(announcement.published_at).toLocaleDateString()}
                </div>
                <div className="actions-cell">
                  <button
                    className="icon-btn"
                    onClick={() => handleEdit(announcement)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    className="icon-btn danger"
                    onClick={() => handleDelete(announcement.id)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageAnnouncements;
